/**
 * Created by PRIME AI.
 * @author Samuel Hinchliffe <samuel@primeai.co.uk, sam.hinchliffe.work@gmail.com>
 * @see    [Linkedin] {@link https://www.linkedin.com/in/samuel-hinchliffe-2bb5801a5/}
 *
 * @summary This is an app. An App encapsulates all the required logic to load in
 * components.
 *
 * Created at: 25/10/2023
 * @see       [PRIME-AI GITHUB] {@link https://github.com/PRIME-AI-GITHUB}
 * @see       [Website]         {@link https://www.primeai.co.uk/}
 * @copyright 2018-2023 PRIME AI LTD
*/

import RecommendationCTA from '../Components/FindClothesByPictures/RecommendationCTA';
import RecommendationWindow from '../Components/FindClothesByPictures/RecommendationWindow';
import {
    Log, App, AppNotAllowedError, CacheStorage,
} from '../../modules/WidgetWorks/WidgetWorks';

export default class RecommendationApp extends App {
    constructor(config, appFactory, productId) {
        super(config, appFactory);

        // ! Remove in 0.6, CTAs hold the product id.
        // App Requires a product id.
        this.productId = productId;
    }

    overrideHook() {}

    preLoad() {}

    postLoad() {
        this.runPostLoadAnalyticsEvent();
    }

    isAppAllowedToRunDueToABTest() {
        if (!this.config.abTest) return true;

        // Does the user already have a ab test flag?
        const flag = localStorage.getItem('PRIME_AI_FCBP_APP_AB_TEST');

        // If the flag is null, it means the AB test is not happening at all.
        if (flag === null || flag === 'false') return false;

        // If the flag is 'true', it means an AB test is going on and the app should not run.
        if (flag === 'true') return true;

        // Enabled in the config and the user has not been assigned a flag.
        if (flag == null) {
            const result = Math.random() < 0.5;
            localStorage.setItem('PRIME_AI_FCBP_APP_AB_TEST', result);
            return result === 'true';
        }

        // Default to true.
        return true;
    }

    async isAppAllowedToRun() {
        if (!this.config.enabled) return { result: false, reason: 'App is disabled in config' };

        // The conditions of the Recommendation App is that
        // it requires to be on a product Page and have a pdp
        // in qDrant for recommendation.
        if (!this.appFactory.isPdpPage) return { result: false, reason: 'Not a PDP Page, this app requires product data' };

        // Product is not found?
        const isProductInStock = await this.appFactory.getProduct(this.productId);
        if (!isProductInStock) return { result: false, reason: 'Product was not found. Config says to disable in this case. ' };

        if (!this.isAppAllowedToRunDueToABTest()) return { result: false, reason: 'AB Test is running, this app is not allowed to run due to the flag being false.' };

        return { result: true, reason: "It's all good!", productDetails: isProductInStock };
    }

    runPostLoadAnalyticsEvent() {
        // Where-in the button / icon is displayed.
        // As we are on a pdp and have a product, we can run this event.
        // As we can see the button.
        this.appFactory.analytics.registerEvent('button_display', 1, 'FCBP');
    }

    runViewAnalyticsEvent() {
        // View / display will happen on every page even if the pdp isn't found
        // as well as running a extra event where we check if the user is unique or not

        // Run the view event.
        this.appFactory.analytics.registerEvent('view', 1, 'FCBP');

        // Check if the user is unique.
        // Unique being defined as a user who has not visited this site today.
        const flagExists = CacheStorage.get('PRIME_AI_FCBP_APP_UNIQUE_USER');

        // If the user is unique, register a unique visitor event.
        // and set a flag to say they are no longer unique.
        if (!flagExists) {
            this.appFactory.analytics.registerEvent('unique_visitors', 1, 'FCBP');
            CacheStorage.set('PRIME_AI_FCBP_APP_UNIQUE_USER', true, CacheStorage.expirationEndOfDay());
        }
    }

    async init() {
        try {
            // Run the over-ride hook.
            this.overrideHook();

            // Initiate the Analytics API & Register a Page View
            // ?  HighLevel App Logic, it's allowed to stay
            // !! @Michael Should we run a event on non-pdp page.
            // this.appFactory.analytics.registerEvent('view', 1, 'FCBP');

            // Run a 'Display' / 'view' on product page and only product pages.
            // even if the product isn't found.
            if (this.appFactory.isPdpPage) this.runViewAnalyticsEvent();

            // Are the apps allowed to run?
            const allowedToRun = await this.isAppAllowedToRun();
            if (!allowedToRun.result) throw new AppNotAllowedError(allowedToRun.reason);
            this.productDetails = allowedToRun.productDetails;

            // Run the preload hook.
            this.preLoad();

            // Load Product Recommendation App.
            await this.loadApp();

            // Run the post-load hook.
            this.postLoad();

            return true;
        } catch (error) {
            if (error instanceof AppNotAllowedError) {
                console.error('Failed to initialize:', error);
                console.log(...Log.w(`${this.constructor.name}`, 'ERROR', `AppNotAllowedError Failed to load 1 or more apps: ${error}`));
                return false;
            }
            this.sentry.captureException(error);
            console.error('Critical Error:', error);
            console.log(...Log.w(`${this.constructor.name}`, 'ERROR', `Critical Error Failed to load 1 or more apps: ${error}`));
            return false;
        }
    }

    async loadApp() {
        // Compile the apps details. Download their URLs, inject their styles, build the elements.
        const windowAppDetails = await this.appFactory.compileApp(this.config.apps.RecommendationWindow);
        const CtaAppDetails = await this.appFactory.compileApp(this.config.apps.RecommendationCTA);

        // Pass the app details to the app to run the app.
        this.recommendationCTA = new RecommendationCTA(CtaAppDetails);
        const ctaLoaded = this.recommendationCTA.init(); // ADD ASYNC IF YOU WANT IT TO WAIT

        this.recommendationWindow = new RecommendationWindow(windowAppDetails, this.productDetails);
        const windowLoaded = this.recommendationWindow.init(); // ADD ASYNC IF YOU WANT IT TO WAIT

        // Build a connection between the apps, similar to an event emitter system.
        this.recommendationWindow.connections.recommendationCTA = this.recommendationCTA;
        this.recommendationCTA.connections.recommendationWindow = this.recommendationWindow;

        // Do not continue until both apps have loaded.
        await Promise.all([ctaLoaded, windowLoaded]);

        return true;
    }
}
